import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import Select from 'react-select';
import styled from 'styled-components';

import UserErrorMessages from './UserErrorMessages';

import { getCurrentOrganization } from 'common/selectors/auth';
import { Message } from 'semantic-ui-react';
import { useApiPortalV1OrganizationListQuery } from 'store/generated/portal/organizations';
import { handleOrgChange } from '../../../routing/utils/LoginUtils';
import { getUserItem } from 'common/utils/userStorage';

const SelectOrg = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: organizations = [] } = useApiPortalV1OrganizationListQuery();
  const currentOrganization = useSelector(getCurrentOrganization);

  const [selectedOrg, setSelectedOrg] = useState(false);

  const [formErrors, setFormErrors] = useState([]);
  const [pristine, setPristine] = useState(true);

  const { get_auth } = useSelector(
    state => ({
      get_auth: state.auth,
    }),
    shallowEqual
  );

  const orgList = useMemo(
    () => organizations.map(org => ({ Name: org.name, OrganizationID: org.id })),
    [organizations]
  );

  // Initialize selected organization from localStorage
  useEffect(() => {
    if (orgList.length > 0 && !selectedOrg) {
      let orgToSelect = null;
      
      // Try to get the previously selected org for this user
      const storedOrgId = getUserItem('selected_org_id');
      if (storedOrgId) {
        orgToSelect = orgList.find(org => org.OrganizationID === parseInt(storedOrgId));
      }
      
      // If stored org not found (e.g., archived) or no stored org, default to first in list
      if (!orgToSelect && orgList.length > 0) {
        orgToSelect = orgList[0];
      }
      
      if (orgToSelect) {
        setSelectedOrg(orgToSelect);
      }
    }
  }, [orgList, selectedOrg]);

  useEffect(() => {
    const ErrorList = [];
    let totalErrors = 0;

    if (orgList.length > 0 && !selectedOrg?.OrganizationID) {
      ErrorList['organization'] = {
        error: true,
        message: 'Please select an Organization',
      };
      totalErrors += 1;
    }
    ErrorList['errors'] = totalErrors;
    setFormErrors(ErrorList);
  }, [orgList, selectedOrg, pristine]);

  const onSubmit = () => {
    if (formErrors?.errors > 0) {
      setPristine(false);
      return;
    }

    // Convert the organization object to match what handleOrgChange expects
    const orgForHandleChange = {
      id: selectedOrg.OrganizationID,
      name: selectedOrg.Name,
    };

    // Update the organization in user context
    handleOrgChange(dispatch, orgForHandleChange);
    navigate('/app/dashboard');
    setPristine(true);
  };

  let { from } = props?.location?.state || {
    from: { pathname: '/app/dashboard' },
  }; // new

  // if both the tokens are same send show the dashboard
  if (get_auth?.token?.trim() === get_auth?.account_default?.verify_token?.trim()) {
    return <Navigate to={from} />;
  }

  // If an organization is already selected, redirect to dashboard
  if (currentOrganization) {
    return <Navigate to="/app/dashboard" />;
  }

  return (
    <Container>
      <FormContainer>
        <Box>
          <UserErrorMessages />
          {formErrors.length > 0 && !pristine && (
            <Message negative header="We need these details!" list={formErrors} />
          )}
          <>
            <Label>Select an Organization</Label>
            <Select
              name="organization"
              options={orgList}
              onChange={setSelectedOrg}
              getOptionLabel={option => `${option.Name} (${option.OrganizationID})`}
              getOptionValue={option => `${option.OrganizationID}`}
              value={selectedOrg}
            />
            {formErrors?.organization?.error && !pristine && (
              <Message error content={formErrors?.organization?.message} />
            )}
          </>
          <Button onClick={onSubmit} id="sign-in">
            Select Org
          </Button>
        </Box>
      </FormContainer>
    </Container>
  );
};

export default SelectOrg;

const Button = styled.button`
  width: 100%;
  height: 40px;
  min-height: 55px;
  border-radius: 8px;
  background-color: #2d50e2;
  color: white;
  font-size: 18px;
  font-family: 'Barlow Semi Condensed', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  outline: none;
  margin-top: 30px;
  &:hover {
    background-color: rgba(0, 0, 0, 0);
    border: 1px solid #2d50e2;
    color: #2d50e2;
    margin-right: 3px;
  }
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Box = styled.div`
  height: 100%;
  background-color: #ffffff;
  padding: 50px;
  display: flex;
  flex-direction: column;
  width: 100%;
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Label = styled.div`
  margin-bottom: 10px;
  font-weight: bold;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 600px;
  height: 100%;
  padding: 100px 30px 0 30px;
  display: flex;
  @media (max-width: 768px) {
    width: 100%;
    padding: 20px 20px 0 20px;
  }
  @media (max-height: 1000px) {
    padding-top: 100px;
  }
`;
